import isNil from 'lodash/isNil';
import type {
  Dayjs,
} from 'dayjs';
import dayjs from 'utils/dayjs';
import type {
  EventType,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import {
  PanelDefinition,
  POSITIVE_INTEGER,
} from 'pages/Dashboard/contexts/sidePanel/panelUrl';

/**
 * `occurrence` is the occurrence start exactly as the calendar gave it,
 * `YYYY-MM-DDTHH:mm:ss`. Recurring kinds need it to find the occurrence.
 * It stays in memory: only appointments are linkable, and they use their
 * fetched start.
 */
export type EventPanelInfo = {
  eventId: number;
  kind: EventType;
  occurrence?: string;
};

const EVENT_KINDS: readonly EventType[] = ['appointment', 'timeBlock', 'availability'];

// Kinds that get a URL and a copy link. The others stay in memory.
const LINKABLE_EVENT_KINDS: readonly EventType[] = ['appointment'];

export const OCCURRENCE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

type EventOpener = {
  id: number | string;
  isTimeBlock?: boolean;
  isAvailability?: boolean;
  // CalendarView sends a Dayjs and VisitsAndNotes a string
  eventDate?: Dayjs | string | null;
};

function getEventKind({ isTimeBlock, isAvailability }: EventOpener): EventType {
  if (isTimeBlock) {
    return 'timeBlock';
  }
  if (isAvailability) {
    return 'availability';
  }
  return 'appointment';
}

export function toEventPanelInfo(opener: EventOpener): EventPanelInfo {
  const eventDate = isNil(opener.eventDate) ? null : dayjs(opener.eventDate);
  return {
    eventId: Number(opener.id),
    kind: getEventKind(opener),
    occurrence: eventDate?.isValid() ? eventDate.format(OCCURRENCE_FORMAT) : undefined,
  };
}

function isEventKind(value: string | null): value is EventType {
  return EVENT_KINDS.includes(value as EventType);
}

export function getEventPanelIdentity({ eventId, kind, occurrence }: EventPanelInfo) {
  return kind === 'appointment'
    ? `${kind}:${eventId}`
    : `${kind}:${eventId}:${occurrence}`;
}

export const eventPanelUrl: NonNullable<PanelDefinition<EventPanelInfo>['url']> = {
  params: ['eventId', 'kind'],
  serialize: ({ eventId, kind }) => ({
    eventId: String(eventId),
    kind,
  }),
  parse: (searchParams) => {
    const eventId = searchParams.get('eventId');
    const kind = searchParams.get('kind');

    return isNil(eventId) || !POSITIVE_INTEGER.test(eventId) || !isEventKind(kind)
      ? null
      : { eventId: Number(eventId), kind };
  },
  isLinkable: ({ kind }) => LINKABLE_EVENT_KINDS.includes(kind),
};
